import { toast } from "react-toastify";

import { RestoreAgenticLogicRepairBaselineResponseBodyType } from "@intelligems/ig-types/functions/agentic-logic/repair-rule";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

/**
 * "Restore the version before Atlas's fixes": reinstates the draft source the
 * repair run pinned before its first rewrite, as a NEW draft revision through
 * the ordinary save path. Nothing destructive — the repaired revision stays
 * recoverable in the job snapshots, and the graph regenerates from the
 * restored source.
 */
const restoreAgenticLogicRepairBaseline = async (
  payload: ApiRequest<undefined, { ruleId: string }>
) => {
  const resp = await api.post<
    RestoreAgenticLogicRepairBaselineResponseBodyType,
    Record<string, never>
  >(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/restore-repair-baseline`,
    {}
  );
  return resp.data;
};

export const useRestoreAgenticLogicRepairBaseline = () => {
  const queryClient = useQueryClient();

  return useMutation<
    RestoreAgenticLogicRepairBaselineResponseBodyType,
    Error,
    ApiRequest<undefined, { ruleId: string }>,
    unknown
  >({
    mutationFn: async (data) => restoreAgenticLogicRepairBaseline(data),
    onSuccess: async (_result, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: agenticLogicKeys.draft(variables.pathParams.ruleId),
        }),
        queryClient.invalidateQueries({
          queryKey: agenticLogicKeys.rule(variables.pathParams.ruleId),
        }),
      ]);
      toast.success("Restored the draft from before Atlas's fixes.");
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to restore the draft${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};
